import { useState, useEffect, useCallback, useRef } from 'react';
import notificationApi from '@/core/notifications/api/notification-api';
import useNotificationSubscription from '@/core/notifications/hooks/use-notification-subscription';
import { MessageServiceConstants, messageService } from '@/core/messaging/export';
import { WidgetNotification } from '@/core/notifications/model/widget-notification';
import {
  NotificationApplication,
  UNREAD_COUNT_CAP,
  UserNotification,
} from '@/core/notifications/model';

interface NotificationPush {
  title?: string;
  body?: string;
}

const notificationSound = new Audio('/sounds/level-up.mp3');

const PAGE_SIZE = 20;

export type NotificationStatusFilter = 'all' | 'unread' | 'read';

export interface UseNotificationInboxResult {
  notifications: UserNotification[];
  applications: NotificationApplication[];
  hasMore: boolean;
  loading: boolean;
  totalUnreadCount: number;

  selectedPublishers: Set<string>;
  selectedSubscriptions: Set<string>;
  statusFilter: NotificationStatusFilter;
  togglePublisher: (id: string) => void;
  toggleSubscription: (id: string) => void;
  setStatusFilter: (status: NotificationStatusFilter) => void;

  loadMore: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markUnread: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotificationInbox(): UseNotificationInboxResult {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [applications, setApplications] = useState<NotificationApplication[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPublishers, setSelectedPublishers] = useState<Set<string>>(new Set());
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<NotificationStatusFilter>('all');
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const cursorRef = useRef<string | undefined>(undefined);
  const listVersionRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  const unreadCountRef = useRef(0);

  const setUnreadCount = useCallback((count: number) => {
    unreadCountRef.current = count;
    setTotalUnreadCount(count);
  }, []);

  const refreshUnreadCount = useCallback(() => {
    notificationApi
      .getNotifications({ status: 'unread', limit: UNREAD_COUNT_CAP })
      .then(page => setUnreadCount(page.items.length))
      .catch(() => {});
  }, [setUnreadCount]);

  useEffect(() => {
    notificationApi
      .getApplications()
      .then(setApplications)
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  const publisher = selectedPublishers.size === 1 ? [...selectedPublishers][0] : undefined;
  const subscription = selectedSubscriptions.size === 1 ? [...selectedSubscriptions][0] : undefined;
  const status = statusFilter !== 'all' ? statusFilter : undefined;

  useEffect(() => {
    const version = ++listVersionRef.current;
    loadingMoreRef.current = false;
    setLoading(true);
    notificationApi
      .getNotifications({ limit: PAGE_SIZE, publisher, subscription, status })
      .then(page => {
        if (version !== listVersionRef.current) return;
        setNotifications(page.items);
        cursorRef.current = page.nextCursor;
        setHasMore(!!page.nextCursor);
      })
      .catch(() => {})
      .finally(() => {
        if (version === listVersionRef.current) setLoading(false);
      });
  }, [publisher, subscription, status]);

  const togglePublisher = useCallback((id: string) => {
    setSelectedPublishers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSubscription = useCallback((id: string) => {
    setSelectedSubscriptions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const loadMore = useCallback(async () => {
    if (!cursorRef.current || loadingMoreRef.current) return;
    const version = listVersionRef.current;
    loadingMoreRef.current = true;
    try {
      const page = await notificationApi.getNotifications({
        limit: PAGE_SIZE,
        cursor: cursorRef.current,
        publisher,
        subscription,
        status,
      });
      if (version !== listVersionRef.current) return;
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.id));
        const fresh = page.items.filter(n => !existingIds.has(n.id));
        return [...prev, ...fresh];
      });
      cursorRef.current = page.nextCursor;
      setHasMore(!!page.nextCursor);
    } catch {
      // Leave the cursor as is so scrolling back to the bottom retries.
    } finally {
      if (version === listVersionRef.current) loadingMoreRef.current = false;
    }
  }, [publisher, subscription, status]);

  // A notification can have several inbox entries (subscription + broadcast); ack/unack
  // applies to all of them, and each one counts towards the unread total.
  const markRead = useCallback(
    async (id: string) => {
      const unreadEntries = notificationsRef.current.filter(
        n => n.notification.id === id && !n.readAt,
      ).length;
      const capped = unreadCountRef.current >= UNREAD_COUNT_CAP;
      const now = new Date().toISOString();
      setNotifications(prev =>
        prev.map(n => (n.notification.id === id ? { ...n, readAt: n.readAt ?? now } : n)),
      );
      if (!capped) setUnreadCount(Math.max(0, unreadCountRef.current - unreadEntries));
      try {
        await notificationApi.ackNotification(id);
        if (capped && unreadEntries > 0) refreshUnreadCount();
      } catch {
        setNotifications(prev =>
          prev.map(n =>
            n.notification.id === id && n.readAt === now ? { ...n, readAt: null } : n,
          ),
        );
        if (!capped) setUnreadCount(unreadCountRef.current + unreadEntries);
      }
    },
    [setUnreadCount, refreshUnreadCount],
  );

  const markUnread = useCallback(
    async (id: string) => {
      const previousReadAt = new Map(
        notificationsRef.current
          .filter(n => n.notification.id === id && !!n.readAt)
          .map(n => [n.id, n.readAt]),
      );
      setNotifications(prev =>
        prev.map(n => (n.notification.id === id ? { ...n, readAt: null } : n)),
      );
      setUnreadCount(Math.min(UNREAD_COUNT_CAP, unreadCountRef.current + previousReadAt.size));
      try {
        await notificationApi.unackNotification(id);
      } catch {
        setNotifications(prev =>
          prev.map(n =>
            previousReadAt.has(n.id) ? { ...n, readAt: previousReadAt.get(n.id)! } : n,
          ),
        );
        if (previousReadAt.size === 0) return;
        if (unreadCountRef.current >= UNREAD_COUNT_CAP) refreshUnreadCount();
        else setUnreadCount(Math.max(0, unreadCountRef.current - previousReadAt.size));
      }
    },
    [setUnreadCount, refreshUnreadCount],
  );

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt ?? now })));
    setUnreadCount(0);
    try {
      await notificationApi.ackAllNotifications();
    } catch {
      setNotifications(prev => prev.map(n => (n.readAt === now ? { ...n, readAt: null } : n)));
      refreshUnreadCount();
    }
  }, [setUnreadCount, refreshUnreadCount]);

  useNotificationSubscription<NotificationPush>(push => {
    messageService.publish<WidgetNotification>(
      MessageServiceConstants.WIDGET_NOTIFICATION_RECEIVED,
      {
        widgetIds: [],
        notification: {
          correlationId: `inapp-${push.title}-${push.body}`,
          title: push.title ?? 'New notification',
          description: push.body ?? '',
          durationMillis: 300_000,
          date: new Date(),
        },
      },
    );

    const soundOn = JSON.parse(
      localStorage.getItem('myMNI.notificationSound') ?? 'true',
    ) as boolean;
    if (soundOn) notificationSound.play().catch(() => {});
    setUnreadCount(Math.min(UNREAD_COUNT_CAP, unreadCountRef.current + 1));

    notificationApi.getNotifications({ limit: PAGE_SIZE }).then(page => {
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.id));
        const fresh = page.items.filter(n => !existingIds.has(n.id));
        return fresh.length > 0 ? [...fresh, ...prev] : prev;
      });
    });
  });

  return {
    notifications,
    applications,
    hasMore,
    loading,
    totalUnreadCount,
    selectedPublishers,
    selectedSubscriptions,
    statusFilter,
    togglePublisher,
    toggleSubscription,
    setStatusFilter,
    loadMore,
    markRead,
    markUnread,
    markAllRead,
  };
}
